// Shared kit for EVERY Aria box (dock, setup popups, calendar interview…):
//   • AriaResizeGrip — a corner grip the user drags to resize the box; the
//     size is stored on root CSS vars (--aria-box-w/h) so ALL boxes stay one
//     consistent size AND honor the user's chosen size. Persisted.
//   • renderEmph — turns *word* markers in Aria's text into a subtle bold so
//     the important word stands out (owner rule 2026-07-12).
import React from 'react';

// Apply any saved box size once, as early as this module is imported.
try {
  const w = localStorage.getItem('aria_box_w');
  const h = localStorage.getItem('aria_box_h');
  if (w) document.documentElement.style.setProperty('--aria-box-w', w);
  if (h) document.documentElement.style.setProperty('--aria-box-h', h);
} catch (_) { /* SSR / no storage */ }

// Eight resize handles hugging the box's edges + corners. `start(dir)` returns
// the pointer-down handler for that direction ('n','s','e','w','ne','nw','se','sw').
export function ResizeEdges({ start }) {
  const H = [
    ['n', 'ns-resize', { top: 0, left: 12, right: 12, height: 7 }],
    ['s', 'ns-resize', { bottom: 0, left: 12, right: 12, height: 7 }],
    ['e', 'ew-resize', { right: 0, top: 12, bottom: 12, width: 7 }],
    ['w', 'ew-resize', { left: 0, top: 12, bottom: 12, width: 7 }],
    ['ne', 'nesw-resize', { top: 0, right: 0, width: 15, height: 15 }],
    ['nw', 'nwse-resize', { top: 0, left: 0, width: 15, height: 15 }],
    ['se', 'nwse-resize', { bottom: 0, right: 0, width: 15, height: 15 }],
    ['sw', 'nesw-resize', { bottom: 0, left: 0, width: 15, height: 15 }],
  ];
  return H.map(([dir, cur, p]) => (
    <div key={dir} onPointerDown={start(dir)} aria-hidden
      style={{ position: 'absolute', cursor: cur, zIndex: 8, touchAction: 'none', ...p }} />
  ));
}

export function AriaResizeGrip() {
  const onDown = (e) => {
    const box = e.currentTarget.closest('.support-chat');
    if (!box) return;
    e.preventDefault();
    const r = box.getBoundingClientRect();
    const startX = e.clientX, startY = e.clientY, startW = r.width, startH = r.height;
    const move = (ev) => {
      // Grip lives bottom-LEFT: dragging left widens, dragging down heightens.
      const w = Math.max(300, Math.min(window.innerWidth - 24, startW + (startX - ev.clientX)));
      const h = Math.max(360, Math.min(window.innerHeight - 40, startH + (ev.clientY - startY)));
      document.documentElement.style.setProperty('--aria-box-w', w + 'px');
      document.documentElement.style.setProperty('--aria-box-h', h + 'px');
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      try {
        localStorage.setItem('aria_box_w', document.documentElement.style.getPropertyValue('--aria-box-w'));
        localStorage.setItem('aria_box_h', document.documentElement.style.getPropertyValue('--aria-box-h'));
      } catch (_) {}
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return <div className="aria-resize-grip" onPointerDown={onDown} title="Drag to resize" aria-hidden />;
}

// *important* → subtly bold. Keeps everything else plain text.
export function renderEmph(text) {
  const s = String(text == null ? '' : text);
  if (s.indexOf('*') === -1) return s;
  return s.split(/(\*[^*]+\*)/g).map((part, i) => {
    if (part.length > 2 && part[0] === '*' && part[part.length - 1] === '*') {
      return <b key={i} className="aria-emph">{part.slice(1, -1)}</b>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}
